/**
 * IssuesList - Displays detected issues with expandable details
 */

import React, {useState} from "react";
import {Card, CardBody, Badge, Collapse, Button, Row, Col} from "reactstrap";
import {Issue, IssueSeverity} from "../../types/advancedAnalysis";
import DOMPurify from "dompurify";

interface IssuesListProps {
  issues: Issue[];
}

const getSeverityColor = (severity: IssueSeverity): string => {
  switch (severity) {
    case "critical":
      return "danger";
    case "error":
      return "warning";
    case "warning":
      return "info";
    case "info":
      return "secondary";
    default:
      return "secondary";
  }
};

const getCategoryIcon = (category: string): string => {
  switch (category) {
    case "graphics":
      return "fa-desktop";
    case "network":
      return "fa-wifi";
    case "sensors":
      return "fa-heartbeat";
    case "hardware":
      return "fa-microchip";
    case "correlation":
      return "fa-project-diagram";
    default:
      return "fa-cog";
  }
};

const IssueCard: React.FC<{issue: Issue}> = ({issue}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => setIsOpen(!isOpen);

  // Sanitize HTML content
  const sanitizedDescription = DOMPurify.sanitize(issue.Description);
  const sanitizedExplanation = issue.Explanation ? DOMPurify.sanitize(issue.Explanation) : "";
  const sanitizedTechnicalDetails = DOMPurify.sanitize(issue.TechnicalDetails);
  const sanitizedUserImpact = DOMPurify.sanitize(issue.UserImpact);

  // Format title with activity names
  const activitySuffix =
    issue.ActivityNames && issue.ActivityNames.length > 0
      ? ` in ${issue.ActivityNames.join(", ")} ${issue.ActivityNames.length === 1 ? "activity" : "activities"}`
      : "";
  const displayTitle = issue.Title + activitySuffix;

  return (
    <Card className="mb-3 issue-card">
      <CardBody>
        <div className="card-header-row">
          <h5 className="mb-2 card-title-text">
            <i className={`fa ${getCategoryIcon(issue.Category)} mr-2`}></i>
            {displayTitle}
          </h5>
          <Button color="link" onClick={toggle} size="sm" className="card-toggle-btn">
            {isOpen ? (
              <>
                <i className="fa fa-chevron-up mr-1"></i>
                Less
              </>
            ) : (
              <>
                <i className="fa fa-chevron-down mr-1"></i>
                More
              </>
            )}
          </Button>
        </div>
        <p className="text-muted mb-2">{issue.Summary || issue.Description}</p>
        <div className="mb-2">
          <small className="text-muted d-block">
            <strong>Severity:</strong> {issue.Severity}
            {issue.AffectedDevices && issue.AffectedDevices.length > 0 && (
              <>
                {" "}
                • <strong>Device:</strong> {issue.AffectedDevices.join(", ")}
              </>
            )}
          </small>
        </div>

        <Collapse isOpen={isOpen}>
          <hr />
          {sanitizedExplanation && (
            <div className="mt-3">
              <h6 className="font-weight-bold">Explanation</h6>
              <div className="analysis-html-content" dangerouslySetInnerHTML={{__html: sanitizedExplanation}} />
            </div>
          )}

          {!sanitizedExplanation && sanitizedDescription && (
            <div className="mt-3">
              <h6 className="font-weight-bold">Description</h6>
              <div className="analysis-html-content" dangerouslySetInnerHTML={{__html: sanitizedDescription}} />
            </div>
          )}

          <div className="mt-3">
            <h6 className="font-weight-bold">User Impact</h6>
            <div className="analysis-html-content" dangerouslySetInnerHTML={{__html: sanitizedUserImpact}} />
          </div>

          <div className="mt-3">
            <h6 className="font-weight-bold">Technical Details</h6>
            <div className="analysis-html-content" dangerouslySetInnerHTML={{__html: sanitizedTechnicalDetails}} />
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
};

const IssuesList: React.FC<IssuesListProps> = ({issues}) => {
  if (issues.length === 0) {
    return (
      <div className="alert alert-success">
        <i className="fa fa-check-circle mr-2"></i>
        No issues detected. Your setup is performing well!
      </div>
    );
  }

  // Group issues by category
  const groupedIssues = issues.reduce(
    (acc, issue) => {
      if (!acc[issue.Category]) {
        acc[issue.Category] = [];
      }
      acc[issue.Category].push(issue);
      return acc;
    },
    {} as Record<string, Issue[]>
  );

  // Sort by priority within each category
  Object.keys(groupedIssues).forEach((category) => {
    groupedIssues[category].sort((a, b) => {
      const priorityOrder = {critical: 0, high: 1, medium: 2, low: 3};
      return priorityOrder[a.Priority] - priorityOrder[b.Priority];
    });
  });

  return (
    <div>
      {Object.entries(groupedIssues).map(([category, categoryIssues]) => (
        <div key={category} className="mb-4">
          <h5 className="text-capitalize mb-3">
            <i className={`fa ${getCategoryIcon(category)} mr-2`}></i>
            {category} Issues ({categoryIssues.length})
          </h5>
          {categoryIssues.map((issue) => (
            <IssueCard key={issue.Id} issue={issue} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default IssuesList;
